import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Button } from '@/components';
import {
  SiMetrics,
  SiMetricsClient,
  scheduledInstantAPI,
} from '@/services/api/scheduled-instant';

import { ClientPicker } from './pickers';
import { Panel, StatCell } from './shared';
import { UnreachableRidersModal } from './UnreachableRidersModal';

const RANGES: [label: string, days: number][] = [
  ['Hari ini', 1],
  ['7 hari', 7],
  ['14 hari', 14],
  ['30 hari', 30],
];

/**
 * Screen 14 — Reliability Metrics.
 *
 * The headline is a PAIR, not one number, and the copy says why. "Waktu Cari
 * Pengganti" only measures escalations that were actually resolved, so giving up
 * on a slot removes a slow datapoint and the median improves. "Berhasil Dalam
 * Target" keeps every escalation in its denominator, so the two together cannot
 * be gamed by giving up faster.
 *
 * What this screen does NOT measure is stated on it. Scheduled Instant records
 * rider commitments, not delivery outcomes — this page has no attendance
 * signal (actual delivery completions are reconciled on the separate
 * "Rekonsiliasi" tab). Without that caption a reader will assume
 * these numbers prove deliveries happened.
 */
export const MetricsView: React.FC = () => {
  const [days, setDays] = useState(14);
  const [client, setClient] = useState('');
  // Provider id only highlights the picker's option; the client NAME is what
  // the endpoint filters on.
  const [providerId, setProviderId] = useState<number | ''>('');
  const [data, setData] = useState<SiMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreachableOpen, setUnreachableOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await scheduledInstantAPI.getMetrics({
          start_date: dayjs()
            .subtract(days - 1, 'day')
            .format('YYYY-MM-DD'),
          end_date: dayjs().format('YYYY-MM-DD'),
          client: client || undefined,
        })
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [days, client]);

  useEffect(() => {
    load();
  }, [load]);

  // Sedang Diam / Menolak follow the range selector but always reach through
  // tomorrow (backend extends the end +1 day) so tonight's already-pinged
  // silent riders still show.
  const attentionWindow =
    days === 1 ? 'hari ini & besok' : `${days} hari + besok`;

  if (loading) {
    return (
      <Panel className="p-10 text-center text-sm text-gray-400">Memuat…</Panel>
    );
  }

  if (error) {
    return (
      <Panel className="p-10 text-center">
        <div className="text-sm text-red-600">{error}</div>
        <Button className="mt-3" onClick={load}>
          Coba lagi
        </Button>
      </Panel>
    );
  }

  if (!data) return null;

  const { replacement: rep } = data;
  // total === 0 means no rider ever needed replacing in range. Distinct from
  // "the numbers are zero" — say so rather than render a wall of dashes.
  const noEscalations = rep.total === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-ink">Reliabilitas Rider</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {dayjs(data.startDate).format('DD MMM')} –{' '}
            {dayjs(data.endDate).format('DD MMM YYYY')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-52">
            <ClientPicker
              open
              providerId={providerId}
              clientName={client}
              placeholder="Semua klien…"
              onChange={(sel) => {
                setProviderId(sel ? sel.providerId : '');
                setClient(sel ? sel.clientName : '');
              }}
            />
          </div>
          <div className="flex gap-1">
          {RANGES.map(([label, d]) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-gsm transition-colors ${
                days === d
                  ? 'bg-accent text-white'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {noEscalations ? (
        <Panel className="p-10 text-center">
          <i className="ri-checkbox-circle-line text-3xl text-green-500" />
          <div className="mt-2 font-semibold text-ink">
            Belum ada rider yang perlu diganti
          </div>
          <div className="text-sm text-gray-400 mt-1">
            Tidak ada eskalasi pada rentang ini, jadi belum ada waktu pemulihan
            untuk diukur.
          </div>
        </Panel>
      ) : (
        <>
          {/* The headline pair. Kept side by side on purpose — reading either
              one alone gives a misleading picture. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel className="border-l-4 border-l-accent">
              <StatCell
                label="Waktu Cari Pengganti (median)"
                testId="metric-median"
                value={rep.medianMinutes === null ? '—' : `${Math.round(rep.medianMinutes)}m`}
                tone={
                  rep.medianMinutes !== null &&
                  rep.medianMinutes <= rep.targetMinutes
                    ? 'green'
                    : 'amber'
                }
                hint={`Target di bawah ${rep.targetMinutes}m · p90 ${
                  rep.p90Minutes === null ? '—' : `${Math.round(rep.p90Minutes)}m`
                }`}
              />
              <Delta
                current={rep.medianMinutes}
                previous={rep.previousMedianMinutes}
                unit="m"
                // Faster recovery is better, so a DROP is the good direction.
                lowerIsBetter
              />
              <div className="px-5 pb-3 text-[11px] text-gray-400">
                Hanya menghitung eskalasi yang{' '}
                <span className="font-semibold">berhasil</span> dapat pengganti.
              </div>
            </Panel>

            <Panel className="border-l-4 border-l-primary">
              <StatCell
                label={`Berhasil Dalam ${rep.targetMinutes}m`}
                testId="metric-within-target"
                value={
                  rep.resolvedRate === null
                    ? '—'
                    : `${Math.round(rep.resolvedRate * 100)}%`
                }
                tone={
                  rep.resolvedRate !== null && rep.resolvedRate >= 0.8
                    ? 'green'
                    : 'red'
                }
                hint={`${rep.resolvedWithinTarget} dari ${rep.total} eskalasi`}
              />
              <div className="px-5 pb-3 text-[11px] text-gray-400">
                Termasuk eskalasi yang{' '}
                <span className="font-semibold">tidak pernah</span> dapat
                pengganti ({rep.total - rep.resolved}).
              </div>
            </Panel>
          </div>

          {/* These three are NOT filtered by the range picker above. The first
              two count what still needs doing — slots today and later,
              including tomorrow's already-pinged riders — because a rider on a
              slot that already ran cannot be chased. */}
          <Panel className="divide-y divide-gray-100 md:divide-y-0 md:flex md:divide-x">
            <div className="md:flex-1">
              <StatCell
                label="Berhasil Dapat Pengganti"
                value={
                  rep.recoveryRate === null
                    ? '—'
                    : `${Math.round(rep.recoveryRate * 100)}%`
                }
                tone={
                  rep.recoveryRate !== null && rep.recoveryRate >= 0.85
                    ? 'green'
                    : 'amber'
                }
                hint={`${rep.resolved} dari ${rep.total} eskalasi · apa pun lamanya`}
                testId="metric-recovery-rate"
              />
              <Delta
                current={rep.recoveryRate}
                previous={rep.previousRecoveryRate}
                unit="pp"
                percentPoints
              />
            </div>
            <div className="md:flex-1">
              <StatCell
                label="Konfirmasi Terlambat"
                value={data.confirmedLate}
                tone="amber"
                hint={`dari  eskalasi · rider akhirnya konfirmasi sendiri, tanpa pengganti`}
                testId="metric-confirmed-late"
              />
            </div>
            <div className="md:flex-1">
              <StatCell
                label="Total Slot Dibuat"
                value={data.totalSlots}
                tone="ink"
                hint={`${days} hari terakhir${client ? ` · ${client}` : ' · seluruh klien'}`}
                testId="metric-total-slots"
              />
            </div>
          </Panel>

          <Panel className="divide-y divide-gray-100 md:divide-y-0 md:flex md:divide-x">
            <div className="md:flex-1">
              <StatCell
                label="Sedang Diam"
                value={data.silentNow}
                tone="red"
                hint={`NO_RESPONSE — perlu dikejar (${attentionWindow})`}
              />
            </div>
            <div className="md:flex-1">
              <StatCell
                label="Menolak"
                value={data.declinedNow}
                tone="amber"
                hint={`TIDAK — perlu pengganti (${attentionWindow})`}
              />
            </div>
            <div className="md:flex-1">
              <StatCell
                label="Nomor Tidak Aktif"
                value={data.unreachableRiders}
                tone="amber"
                hint={
                  data.unreachableRiders > 0
                    ? 'Pesan gagal terkirim — klik untuk lihat rider'
                    : 'Pesan gagal terkirim, bukan diam'
                }
                testId="metric-unreachable"
                onClick={
                  data.unreachableRiders > 0
                    ? () => setUnreachableOpen(true)
                    : undefined
                }
              />
            </div>
          </Panel>

          <Panel>
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-ink text-sm">
                Rider Balas Duluan
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Dari pesan yang benar-benar terkirim. Nomor yang gagal dikirimi
                tidak dihitung.
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="text-left px-5 py-2.5 font-semibold">
                    Checkpoint
                  </th>
                  <th className="text-right px-5 py-2.5 font-semibold">
                    Terkirim
                  </th>
                  <th className="text-right px-5 py-2.5 font-semibold">YA</th>
                  <th className="text-right px-5 py-2.5 font-semibold">
                    TIDAK
                  </th>
                  <th className="text-right px-5 py-2.5 font-semibold">Diam</th>
                  <th className="text-right px-5 py-2.5 font-semibold">
                    Respons
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.checkpoints.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-6 text-center text-sm text-gray-400"
                    >
                      Belum ada pesan konfirmasi terkirim pada rentang ini.
                    </td>
                  </tr>
                )}
                {data.checkpoints.map((c) => (
                  <tr key={c.phase} className="border-b border-gray-50">
                    <td className="px-5 py-3 font-semibold text-ink">
                      {c.phase === 'T12' ? 'T-12' : 'T-2'}
                    </td>
                    <td className="px-5 py-3 text-right tabular text-gray-600">
                      {c.delivered}
                    </td>
                    <td className="px-5 py-3 text-right tabular text-green-600">
                      {c.confirmed}
                    </td>
                    <td className="px-5 py-3 text-right tabular text-amber-600">
                      {c.declined}
                    </td>
                    <td className="px-5 py-3 text-right tabular text-red-600">
                      {c.silent}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Badge
                        tone={
                          c.responseRate !== null && c.responseRate >= 0.8
                            ? 'success'
                            : 'amber'
                        }
                        size="small"
                        tabular
                      >
                        {c.responseRate === null
                          ? '—'
                          : `${Math.round(c.responseRate * 100)}%`}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <PerClientTable rows={data.perClient} t2Enabled={data.t2Enabled} />

          <TrendChart data={data} />

          <p className="text-[11px] text-gray-400 leading-relaxed">
            <i className="ri-information-line mr-1" />
            Angka di halaman ini dihitung dari{' '}
            <span className="font-semibold">komitmen rider</span> — apa yang
            rider jawab dan apa yang ops lakukan — jadi halaman ini{' '}
            <span className="font-semibold">tidak</span> membuktikan pengiriman
            benar terjadi. Untuk mencocokkan penugasan slot dengan pengiriman
            yang benar-benar selesai, lihat tab{' '}
            <span className="font-semibold">Rekonsiliasi</span>.
          </p>

          <UnreachableRidersModal
            open={unreachableOpen}
            onClose={() => setUnreachableOpen(false)}
            startDate={dayjs()
              .subtract(days - 1, 'day')
              .format('YYYY-MM-DD')}
            endDate={dayjs().format('YYYY-MM-DD')}
            client={client || undefined}
          />
        </>
      )}
    </div>
  );
};

/**
 * Escalated vs recovered per day. Deliberately a plain stacked bar rather than a
 * charting dependency: the shape ops needs is "how big is the gap between the
 * two bars", which does not justify a new library.
 */
const TrendChart: React.FC<{ data: SiMetrics }> = ({ data }) => {
  const max = Math.max(1, ...data.trend.map((d) => d.escalated));
  return (
    <Panel>
      <div className="px-5 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-ink text-sm">Eskalasi per Hari</h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Batang penuh = eskalasi. Hijau = dapat pengganti, oranye = rider konfirmasi terlambat, merah = tidak tertangani.
        </p>
      </div>
      <div className="px-5 py-4">
        {data.trend.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-4">
            Belum ada data.
          </div>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {data.trend.map((d) => {
              const h = (d.escalated / max) * 100;
              const rec = d.escalated > 0 ? (d.recovered / d.escalated) * 100 : 0;
              const late =
                d.escalated > 0 ? (d.confirmedLate / d.escalated) * 100 : 0;
              return (
                <div
                  key={d.scheduleDate}
                  className="flex-1 h-full flex flex-col items-center gap-1 group"
                  title={`${dayjs(d.scheduleDate).format('DD MMM')} · ${d.escalated} eskalasi: ${d.recovered} pengganti, ${d.confirmedLate} terlambat, ${d.escalated - d.recovered - d.confirmedLate} tidak tertangani`}
                >
                  {/* Bar sits in a flex-1 track so its % height resolves against
                      a real height — without this the column is auto-height and
                      every bar collapses to 0px (invisible chart). */}
                  <div className="flex-1 w-full flex items-end min-h-0">
                    <div
                      className="w-full bg-red-200 rounded-t relative overflow-hidden"
                      style={{ height: `${Math.max(h, 3)}%` }}
                    >
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-green-400"
                        style={{ height: `${rec}%` }}
                      />
                      {/* Late confirmations stack on top of replacements; whatever red
                          remains above is the escalations nobody handled. */}
                      <div
                        className="absolute left-0 right-0 bg-amber-300"
                        style={{ bottom: `${rec}%`, height: `${late}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-[9px] text-gray-400 tabular">
                    {dayjs(d.scheduleDate).format('DD')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Panel>
  );
};

/**
 * Period-over-period movement.
 *
 * Renders nothing when there is no comparable previous value — a "0%" or a flat
 * arrow would claim the metric held steady when in fact we have nothing to
 * compare against, which is a different and much weaker statement.
 *
 * `lowerIsBetter` exists because the two metrics point opposite ways: a falling
 * median is good (faster recovery), a falling recovery rate is bad. Colour has
 * to follow meaning, not sign.
 */
const Delta: React.FC<{
  current: number | null;
  previous: number | null;
  unit: string;
  lowerIsBetter?: boolean;
  /** Format the difference as percentage points rather than a raw number. */
  percentPoints?: boolean;
}> = ({ current, previous, unit, lowerIsBetter, percentPoints }) => {
  if (current === null || previous === null) {
    return (
      <div className="px-5 pb-1 text-[11px] text-gray-400">
        Belum ada pembanding periode sebelumnya
      </div>
    );
  }
  const rawDiff = current - previous;
  const diff = percentPoints ? rawDiff * 100 : rawDiff;
  const rounded = percentPoints ? Math.round(diff * 10) / 10 : Math.round(diff);
  if (rounded === 0) {
    return (
      <div className="px-5 pb-1 text-[11px] text-gray-400">
        Sama dengan periode sebelumnya
      </div>
    );
  }
  const better = lowerIsBetter ? rounded < 0 : rounded > 0;
  const prevLabel = percentPoints
    ? `${Math.round(previous * 100)}%`
    : `${Math.round(previous)}${unit}`;
  return (
    <div
      className={`px-5 pb-1 text-[11px] font-semibold ${
        better ? 'text-green-600' : 'text-red-600'
      }`}
    >
      <i className={rounded < 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} />{' '}
      {Math.abs(rounded)}
      {unit} dari periode sebelumnya ({prevLabel})
    </div>
  );
};

type SortKey =
  | 'clientName'
  | 'medianMinutes'
  | 'recoveryRate'
  | 'noShowRateT12'
  | 'noShowRateT2'
  | 'totalSlots';

/**
 * Per-client breakdown — which clients are dragging the averages.
 *
 * Sorted client-side: there are ~19 clients, so shipping the whole set and
 * sorting in the browser beats a round trip per column click.
 *
 * Rows whose rates came back null had too few slots to say anything. They are
 * still listed, because a client vanishing from this table would read as an
 * outage rather than as thin data — and they sort last regardless of direction,
 * so they never occupy the "worst offender" slot at the top.
 */
const PerClientTable: React.FC<{
  rows: SiMetricsClient[];
  /** T-2 column is hidden when the re-confirm ping is switched off. */
  t2Enabled: boolean;
}> = ({ rows, t2Enabled }) => {
  const [sort, setSort] = useState<SortKey>('medianMinutes');
  const [desc, setDesc] = useState(true);

  const sorted = [...rows].sort((a, b) => {
    if (sort === 'clientName') {
      return desc
        ? b.clientName.localeCompare(a.clientName)
        : a.clientName.localeCompare(b.clientName);
    }
    const av = a[sort];
    const bv = b[sort];
    // Nulls (insufficient data) always sink, whichever way the column points.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return desc ? bv - av : av - bv;
  });

  const header = (key: SortKey, label: string, align = 'right') => (
    <th
      className={`px-4 py-2.5 font-semibold cursor-pointer select-none hover:text-ink transition-colors text-${align}`}
      onClick={() => {
        if (sort === key) setDesc(!desc);
        else {
          setSort(key);
          setDesc(true);
        }
      }}
    >
      {label}
      <span className="ml-1 text-gray-300">
        {sort === key ? (desc ? '▼' : '▲') : '⇅'}
      </span>
    </th>
  );

  const pct = (v: number | null) =>
    v === null ? null : `${Math.round(v * 1000) / 10}%`;

  return (
    <Panel>
      <div className="px-5 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-ink text-sm">Per Klien</h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Klik judul kolom untuk mengurutkan. Lihat kolom Total Slot untuk menilai
          seberapa besar sampel tiap klien.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
              {header('clientName', 'Klien', 'left')}
              {header('medianMinutes', 'Waktu Cari Pengganti')}
              {header('recoveryRate', 'Berhasil Dapat Pengganti')}
              {header('noShowRateT12', 'Nggak Konfirmasi (T-12)')}
              {t2Enabled &&
                header('noShowRateT2', 'Konfirmasi Lalu Hilang (T-2)')}
              {header('totalSlots', 'Total Slot')}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-gray-400"
                >
                  Belum ada slot pada rentang ini.
                </td>
              </tr>
            )}
            {sorted.map((c) => (
              <tr
                key={c.clientName}
                className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 font-medium text-ink">
                  {c.clientName}
                </td>
                <td className="px-4 py-3 text-right tabular text-gray-700">
                  {c.medianMinutes === null
                    ? '—'
                    : `${Math.round(c.medianMinutes)} mnt`}
                </td>
                <td className="px-4 py-3 text-right">
                  {c.recoveryRate === null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <Badge
                      tone={c.recoveryRate >= 0.85 ? 'success' : 'amber'}
                      size="small"
                      tabular
                    >
                      {pct(c.recoveryRate)}
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular text-gray-700">
                  {pct(c.noShowRateT12) ?? '—'}
                </td>
                {t2Enabled && (
                  <td className="px-4 py-3 text-right tabular text-gray-700">
                    {pct(c.noShowRateT2) ?? '—'}
                  </td>
                )}
                <td className="px-4 py-3 text-right tabular text-gray-500">
                  {c.totalSlots}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};
